import { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { convertObjectFieldsToVariables } from '@libraryHelpers/typeAdapters'
import { ScriptValueDialog } from '@microservices/wiskey-react-components'
import {
  IconPickerField,
  VectorIcon,
  VectorIconLibrary,
} from '@microservices/wiskey-react-components'
import {
  buildListItemItemScriptFieldsInfo,
  buildListItemItemScriptVariables,
  ICON_REGISTRY,
} from '@mobileConstants'
import { ColorPickerField } from '@components/ColorPicker'
import { useMobileGlobalVariables } from '@mobileHooks'
import { MobileListItem } from '@mobileTypes'
import { Code as CodeIcon, Edit as EditIcon } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material'

import { useFetchAllDisplayRulesQuery } from '@redux/api/displayRules.api'
import { useFetchMultipleObjectsByCodeQuery } from '@redux/api/objects.api'

import { ShortDisplayRule } from '@types'

export const IconConfigSection = () => {
  const { control, watch, setValue } = useFormContext<MobileListItem>()
  const [showScriptDialog, setShowScriptDialog] = useState(false)
  const [prevIsJS, setPrevIsJS] = useState<boolean | undefined>()

  const isJS = watch('icon.isJS')
  const iconData = watch('icon')
  const getDataCommands = watch('getDataCommands')
  const localVariables = watch('variables') || []
  const { variables: globalVariables = [] } = useMobileGlobalVariables()

  // Fetch all display rules
  const { data: displayRules = [] } = useFetchAllDisplayRulesQuery({})

  // Создаем информацию о командах с их objectCode
  const commandsInfo = useMemo(() => {
    if (!getDataCommands?.length) return []

    return getDataCommands
      .map((cmd, index) => ({
        index,
        commandKey: `command${index + 1}`, // Используем индекс для ключа
        commandName: cmd.name || `command${index + 1}`, // Имя команды для отображения
        objectCode: cmd.objectCode,
      }))
      .filter(info => info.objectCode)
  }, [getDataCommands])

  // Собираем все objectCodes для одного запроса
  const objectCodes = useMemo(() => commandsInfo.map(info => info.objectCode), [commandsInfo])

  // Один запрос для всех объектов
  const { data: objectModels = [] } = useFetchMultipleObjectsByCodeQuery(objectCodes, {
    skip: objectCodes.length === 0,
  })

  const objectFields = useMemo(() => {
    const allFields: Variable[] = []

    commandsInfo.forEach((commandInfo, index) => {
      const objectModel = objectModels.find(model => model.code === commandInfo.objectCode)

      if (objectModel?.fields) {
        const convertedFields = convertObjectFieldsToVariables(objectModel?.fields)
        allFields.push({
          name: commandInfo.commandKey,
          type: convertedFields,
          isArray: true,
          description: commandInfo.objectCode,
        })
      }
    })

    return allFields
  }, [commandsInfo, objectModels])

  const iconConfigScriptVariables = useMemo(
    () =>
      buildListItemItemScriptVariables(localVariables, globalVariables, {
        commandFields: objectFields,
        includeList: true,
      }),
    [objectFields, localVariables, globalVariables]
  )

  const iconConfigScriptFieldsInfo = useMemo(
    () =>
      buildListItemItemScriptFieldsInfo(localVariables, globalVariables, {
        commandFields: objectFields,
        includeList: true,
      }),
    [objectFields, localVariables, globalVariables]
  )

  useEffect(() => {
    if (prevIsJS !== undefined && prevIsJS !== isJS) {
      setValue(
        'icon',
        {
          value: '',
          isJS,
        },
        { shouldDirty: true }
      )
    }
    setPrevIsJS(isJS)
  }, [isJS, prevIsJS, setValue])

  const handleOpenScriptDialog = () => {
    setShowScriptDialog(true)
  }

  const handleCloseScriptDialog = () => {
    setShowScriptDialog(false)
  }

  const handleSaveScript = (script: string) => {
    setValue(
      'icon',
      {
        value: script,
        isJS: true,
      },
      { shouldDirty: true }
    )
  }

  return (
    <Box>
      <Typography variant='subtitle2' sx={{ mb: 2 }}>
        Icon Configuration
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name='icon.isJS'
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    onChange={e => field.onChange(e.target.checked)}
                  />
                }
                label='JS'
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          {!isJS ? (
            <Controller
              name='icon'
              control={control}
              render={({ field }) => {
                let iconVal: VectorIcon | null = null

                if (field.value && !field.value.isJS) {
                  if (field.value.type && field.value.value && field.value.size) {
                    iconVal = {
                      type: field.value.type as VectorIconLibrary,
                      value: field.value.value,
                      size: field.value.size,
                      ...(field.value.color != null && field.value.color !== '' && { color: field.value.color }),
                    }
                  }
                }

                return (
                  <IconPickerField
                    value={iconVal}
                    onChange={icon => {
                      if (icon) {
                        field.onChange({
                          type: icon.type,
                          value: icon.value,
                          size: icon.size,
                          color: icon.color ?? field.value?.color,
                          isJS: false,
                        })
                      } else {
                        field.onChange(null)
                      }
                    }}
                    iconRegistry={ICON_REGISTRY}
                  />
                )
              }}
            />
          ) : (
            <Box>
              <Button
                variant='outlined'
                fullWidth
                startIcon={<CodeIcon />}
                onClick={handleOpenScriptDialog}
              >
                Edit Icon Script
              </Button>
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <Controller
            name='icon.displayRule'
            control={control}
            render={({ field }) => (
              <Autocomplete<ShortDisplayRule>
                options={displayRules}
                getOptionLabel={option => `${option.title} (${option.objectCode})`}
                value={field.value || null}
                onChange={(_, newValue) => {
                  field.onChange(newValue)
                }}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Display Rule (Optional)'
                    placeholder='Select a display rule for icon styling'
                    helperText='Apply dynamic styling to icon based on item data'
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>

      {showScriptDialog && (
        <ScriptValueDialog
          isShow
          value={typeof iconData?.value === 'string' ? iconData.value : ''}
          title='Edit Icon Configuration Script'
          hint='Return an icon object with type, value, size, and optional color properties.'
          onClose={handleCloseScriptDialog}
          onSave={handleSaveScript}
          language='js'
          variables={iconConfigScriptVariables}
          fieldsInfo={iconConfigScriptFieldsInfo}
          examples={[
            {
              title: 'Basic Icon',
              description: 'Return a simple icon configuration',
              code: `return {
  type: "MaterialDesignIcons",
  value: "home",
  size: 24,
  color: "#000000"
}`,
              category: 'Basic',
            },
            {
              title: 'Conditional Icon',
              description: 'Choose icon based on current item',
              code: `// Use current item from data source
if (item.status === 'active') {
  return {
    type: "MaterialDesignIcons",
    value: "check-circle",
    size: 24
  }
} else {
  return {
    type: "MaterialDesignIcons",
    value: "alert-circle",
    size: 24
  }
}`,
              category: 'Conditional',
            },
            {
              title: 'From Item Field',
              description: 'Use icon name from current item',
              code: `// Use field from current item
return {
  type: "MaterialDesignIcons",
  value: item.iconName || "star",
  size: 24
}`,
              category: 'Dynamic',
            },
            {
              title: 'From Local Variable',
              description: 'Use list item local variable',
              code: `return {
  type: "MaterialDesignIcons",
  value: localVariables.showCheckIcon ? "check-circle" : "circle-outline",
  size: 24
}`,
              category: 'Variables',
            },
            {
              title: 'From Global Variable',
              description: 'Use global application variable',
              code: `return {
  type: "MaterialDesignIcons",
  value: globalVariables.defaultIcon || "star",
  size: 24
}`,
              category: 'Variables',
            },
            {
              title: 'Different Icon Libraries',
              description: 'Use different icon types',
              code: `// Available types:
// - MaterialDesignIcons
// - Ionicons
// - FontAwesome
// - Feather

return {
  type: "Ionicons",
  value: "airplane",
  size: 24
}`,
              category: 'Advanced',
            },
          ]}
        />
      )}
    </Box>
  )
}
